// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@synd/App/Components/Notify";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    // XNG-Changes
    const adjustedCartLineId = (state as any).xCart?.adjustedCartLineId;
    return {
        cart,
        // XNG-Changes
        productsCannotBePurchased: cart?.cartLines?.some(o => o.isRestricted || !o.isActive || o.cantBuy),
        cartNotPriced: cart?.cartNotPriced,
        hasInsufficientInventory: cart?.hasInsufficientInventory && cart?.fulfillmentMethod === "Ship",
        hasInvalidPrice: hasProductsWithInvalidPrice(cart),
        failedToGetRealTimeInventory: cart?.failedToGetRealTimeInventory,
        anyLineQtyAdjusted: !!adjustedCartLineId && cart?.cartLines?.some(o => o.id === adjustedCartLineId),
        // TODO
        priceAligned: cart?.properties.priceAligned,
        // XNG-Changes:
        itemDiscountAvailable: cart?.cartLines?.some(
            o => parseInt(o.properties.purchaseMoreDiscountQty || "0", 10) > 0,
        ),
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({
    cart,
    // XNG-Changes
    productsCannotBePurchased,
    cartNotPriced,
    hasInsufficientInventory,
    hasInvalidPrice,
    failedToGetRealTimeInventory,
    anyLineQtyAdjusted,
    priceAligned,
    itemDiscountAvailable,
}: Props) => {
    // XNG-Changes: remove !cart.isAwaitingApproval so we can show other messages
    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    return (
        <>
            {cart.isAwaitingApproval && (
                <Notify type="info" showClose={false} data-test-selector="cart_approvingOrderMessage" fullWidth={true}>
                    {translate("Approving Order for {0}", cart.initiatedByUserName)}
                </Notify>
            )}
            {/* XNG-Changes */}
            {productsCannotBePurchased && (
                <Notify
                    type="danger"
                    showClose={false}
                    data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                    fullWidth={true}
                >
                    {siteMessage("Cart_ProductsCannotBePurchased")}
                </Notify>
            )}
            {cartNotPriced && (
                <Notify
                    type="warning"
                    showClose={false}
                    fullWidth={true}
                    data-test-selector="cartlineHeader_NoPriceAvailableAtCheckout"
                >
                    {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                </Notify>
            )}
            {hasInsufficientInventory && (
                <Notify
                    type="danger"
                    showClose={false}
                    data-test-selector="cartlineHeader_warningInsufficientInventoryAtCheckout"
                    fullWidth={true}
                >
                    {siteMessage("Cart_InsufficientInventoryAtCheckout")}
                </Notify>
            )}
            {!cartNotPriced && hasInvalidPrice && (
                <Notify type="warning" showClose={false} fullWidth={true}>
                    {siteMessage("Cart_InvalidPriceAtCheckout")}
                </Notify>
            )}
            {failedToGetRealTimeInventory && (
                <Notify type="danger" showClose={false} fullWidth={true}>
                    {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                </Notify>
            )}
            {anyLineQtyAdjusted && (
                <Notify type="warning" showClose={false} fullWidth={true}>
                    {siteMessage("Custom_Cart_LineQuantityAdjusted")}
                </Notify>
            )}
            {/* XNG-Changes: TODO */}
            {priceAligned && (
                <Notify type="warning" showClose={false} fullWidth={true}>
                    {siteMessage("Custom_Cart_PriceAligned")}
                </Notify>
            )}

            {/* XNG-Changes: */}
            {itemDiscountAvailable && (
                <Notify type="info" showClose={false} fullWidth={true}>
                    {siteMessage("Custom_Discount_Items_Available")}
                </Notify>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Approver Message",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
